import {initCustomer, loadCustomer} from './features/customer.js';
import {initCurrency} from './features/currency.js';
import {initGiftCardInput} from './features/giftCard.js';
import {initCouponInput} from './features/coupon.js';
import {initShipping} from './features/shipping.js';
import {initLanguage} from './features/language.js';
import {initCart} from './features/cart.js';
import {initStripeSupport} from './payment/stripe.js';
import {initPayPalSupport} from './payment/paypal.js';
import {initMerchantAccount} from './features/account.js';
import {initSummary} from './features/summary.js';
import {initOrderNotes} from './features/orderNotes.js';
import {initVAT} from './features/vat.js';
import {initDeliveryDate} from './features/orderDelivery.js';
import {getOrderService, requestCartCalculation} from './payment/order.js';
import {initModal} from './features/modal.js';
import {initMetrics} from './features/metrics.js';

import {store} from './store.js';
import {MerchantConfiguration, updateMerchantHostName, updateMerchantName} from './reducers/merchantConfigurationReducer.js';
import {Environment, Feature, FeatureFlag, setFeatureSupport, updateEnvironment, updateLanguage} from './reducers/environmentReducer.js';
import {updateSessionId} from './reducers/peachPayOrderReducer.js';
import {DefaultCart} from './reducers/cartReducer.js';
import {determinePageType, getBaseURL} from './util/environment.js';
import {$qs, onWindowMessage} from './util/dom.js';
import {InitMessage} from './models/WindowMessages.js';
import {ISession} from './models/ISession.js';
import {renderAdditionalFields} from './features/additionalFields.js';
import {japaneseFormHTML, defaultFormHTML, checkoutFormNoPhoneNoApt} from './util/languageForms.js';
import {initLinkedProducts} from './features/linked-products.js';

import {GLOBAL} from './deprecated/global.js';
import {sendCartItems} from './deprecated/cart.js';
/**
 * Entry point for the peachpay checkout.
 */
(function () {
	/**
	 * Inits data from the php side.
	 */
	onWindowMessage<InitMessage>('init', async message => {
		store.dispatch(updateMerchantHostName(message.merchantHostname));

		// It is important this function runs first because event listeners depend on the HTML it adds.
		// However, we need to know the merchant hostname in some cases.
		addFormFields(message.phpData.language);

		GLOBAL.quantity = message.quantity;
		GLOBAL.isGroupedProduct = message.isGroupedProduct;
		GLOBAL.phpData = message.phpData;

		if (message.browserLocale) {
			GLOBAL.browserLocaleForStripe = message.browserLocale;
		}

		// Backwards compatibility for old plugins when test mode is enabled and no database entry exist.
		if (typeof GLOBAL.phpData.has_valid_key === 'undefined') {
			GLOBAL.phpData.has_valid_key = true;
		}

		store.dispatch(updateMerchantName(message.phpData.merchant_name));

		store.dispatch(setFeatureSupport(message.phpData.feature_support, message.phpData));
		store.dispatch(updateLanguage(message.phpData.language === 'detect-from-page' ? message.pageLanguage : message.phpData.language));
		store.dispatch(updateEnvironment({
			pluginIsTestMode: Boolean(message.isTestMode),
			pluginPageType: determinePageType(message.isCartPage, message.isCheckoutPage),
			customerIsMobile: message.isMobile,
			pluginButtonColor: message.phpData.button_color,
			pluginVersion: message.phpData.version,
		}));

		/**
		 * Feature callbacks to perform a action or insert data into the redux store once the data is available.
		 */
		initModal();
		initDeliveryDate();
		initMetrics();
		initLinkedProducts();
		initOrderNotes();
		initLanguage(message);
		initSummary(message);
		initCart(message);
		initCouponInput(message);
		initGiftCardInput(message);
		initShipping(message);
		initCustomer(message);
		initCurrency(message);
		initMerchantAccount(message);
		initVAT(message);

		if (Feature.enabled(FeatureFlag.ADDITIONAL_FIELDS)) {
			renderAdditionalFields(message.phpData?.additional_fields ?? [], message.phpData?.additional_fields_order ?? []);
		}

		// Payment Processor support

		const orderService = getOrderService();

		await initStripeSupport(message, orderService);
		await initPayPalSupport(message, orderService);

		// Create a peachpay session
		const sessionResponse = await fetch(getBaseURL(MerchantConfiguration.hostName(), Environment.testMode()) + 'session');
		const session: ISession = await sessionResponse.json() as ISession;
		store.dispatch(updateSessionId(session.id));

		// @deprecated but needed until we send the hostname and other details with the above session creation.
		await sendCartItems(DefaultCart.contents(), session.id);

		// Gets existing customer information if it exist from local storage and maps it to the redux store
		loadCustomer();

		await requestCartCalculation();

		window.top?.postMessage('cartReady', '*');
		window.top?.postMessage('loaded', '*');
	});
})();

function addFormFields(lang: string): void {
	let form = defaultFormHTML;

	if (lang === 'ja') {
		form = japaneseFormHTML;
	}

	if (MerchantConfiguration.hostName() === 'initialaudio.com') {
		form = checkoutFormNoPhoneNoApt;
	}

	$qs('#pp-info')?.insertAdjacentHTML('beforeend', form);
}
